/**
 * Orders View Component - Extracted from CRMTab
 * Handles order listing, filtering, CRUD operations
 */
import React, { useEffect, useState } from 'react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Download,
  RefreshCw,
  Upload,
  Receipt,
  FileStack,
  X,
} from 'lucide-react';
import { SortableTableHeader } from './SortableTableHeader';
import { Pagination } from './Pagination';
import { ORDER_STATUSES, PAYMENT_STATUSES, STATUS_COLORS, PAYMENT_COLORS } from './crmConstants';
import { getSensitiveTopicLabel } from '../../utils/sensitiveTopicsTranslations';
import { isAdLabelYes } from '../../utils/adLabel';

export const OrdersView = ({
  orders,
  pagination,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  paymentFilter,
  setPaymentFilter,
  invoiceFilter,
  setInvoiceFilter,
  invoiceNumberFilter,
  setInvoiceNumberFilter,
  customerFilter,
  setCustomerFilter,
  customersList,
  sortField,
  sortOrder,
  handleSort,
  handleSearch,
  fetchOrders,
  openOrderDialog,
  handleDeleteOrder,
  handleStatusChange,
  handleUpdateOrderStatus,
  viewOrderExpense,
  handleExportOrders,
  setOrderImportDialogOpen,
  onInvoiceSelected,
  selectionResetKey,
  language,
  t,
}) => {
  const isEs = language === 'es';
  // Selected orders persist across pages: id -> order
  const [selected, setSelected] = useState({});
  useEffect(() => { setSelected({}); }, [selectionResetKey]);
  const selectedList = Object.values(selected);
  const selectedTotal = selectedList.reduce((sum, o) => sum + (parseFloat(o.pvp) || 0), 0);
  const selectedCustomers = new Set(selectedList.map(o => o.customer_id || (o.customer_email || '').trim().toLowerCase() || (o.customer_name || '').trim().toLowerCase()));
  const canSelect = (o) => (o.invoice_status || 'sin_factura') === 'sin_factura';
  const selectable = orders.filter(canSelect);
  const allPageSelected = selectable.length > 0 && selectable.every(o => selected[o.id]);
  const toggleOrder = (o) => setSelected(prev => {
    const next = { ...prev };
    if (next[o.id]) delete next[o.id]; else next[o.id] = o;
    return next;
  });
  const togglePage = (v) => setSelected(prev => {
    const next = { ...prev };
    selectable.forEach(o => { if (v) next[o.id] = o; else delete next[o.id]; });
    return next;
  });
  const invoiceSelected = () => onInvoiceSelected(selectedList);

  return (
    <div className="w-screen ml-[calc(-50vw+50%)] px-4 sm:px-6 lg:px-8 space-y-4 overflow-x-hidden" data-testid="orders-view-fullwidth">
      {/* Filters */}
      <Card className="p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t.allStatuses} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allStatuses}</SelectItem>
              {ORDER_STATUSES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={customerFilter || 'all'} onValueChange={(v) => { setCustomerFilter(v === 'all' ? '' : v); }}>
            <SelectTrigger className="w-[180px]" data-testid="customer-filter">
              <SelectValue placeholder={language === 'es' ? 'Todos los clientes' : 'All customers'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'es' ? 'Todos los clientes' : 'All customers'}</SelectItem>
              {(customersList || []).map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={paymentFilter} onValueChange={(v) => { setPaymentFilter(v === 'all' ? '' : v); }}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder={t.allPayments} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allPayments}</SelectItem>
              {PAYMENT_STATUSES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={invoiceFilter || 'all'} onValueChange={(v) => { setInvoiceFilter(v === 'all' ? '' : v); }}>
            <SelectTrigger className="w-[180px]" data-testid="invoice-status-filter">
              <SelectValue placeholder={language === 'es' ? 'Estado factura' : 'Invoice status'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'es' ? 'Todos (factura)' : 'All (invoice)'}</SelectItem>
              <SelectItem value="sin_factura">{language === 'es' ? 'Sin factura' : 'Not invoiced'}</SelectItem>
              <SelectItem value="facturado_pendiente">{language === 'es' ? 'Facturado / Pdte. cobro' : 'Invoiced / Unpaid'}</SelectItem>
              <SelectItem value="facturado_cobrado">{language === 'es' ? 'Facturado / Cobrado' : 'Invoiced / Paid'}</SelectItem>
            </SelectContent>
          </Select>
          <div className="w-[180px]">
            <Input
              placeholder={language === 'es' ? 'Nº factura ingreso' : 'Invoice number'}
              value={invoiceNumberFilter || ''}
              onChange={(e) => setInvoiceNumberFilter(e.target.value)}
              data-testid="invoice-number-filter"
            />
          </div>
          <Button type="submit"><Search className="h-4 w-4" /></Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => { setSearch(''); setStatusFilter(''); setPaymentFilter(''); setInvoiceFilter(''); setInvoiceNumberFilter(''); setCustomerFilter(''); fetchOrders(1); }} 
            className="hover:bg-slate-100"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </form>
      </Card>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <p className="text-slate-500 font-medium">
          {pagination.total} {t.orders}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportOrders} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400">
            <Download className="h-4 w-4 mr-2" />
            {t.export}
          </Button>
          <Button onClick={() => openOrderDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md">
            <Plus className="h-4 w-4 mr-2" />
            {t.newOrder}
          </Button>
          <Button onClick={() => setOrderImportDialogOpen(true)} variant="outline" className="border-indigo-300 text-indigo-600 hover:bg-indigo-50">
            <Upload className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Importar Pedidos' : 'Import Orders'}
          </Button>
        </div>
      </div>

      {onInvoiceSelected && selectedList.length > 0 && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 shadow-sm" data-testid="orders-bulk-invoice-bar">
          <p className="text-sm text-emerald-900">
            <b data-testid="orders-selected-count">{selectedList.length}</b> {isEs ? 'pedidos seleccionados' : 'orders selected'} · <b>€{selectedTotal.toFixed(2)}</b>
          </p>
          {selectedCustomers.size > 1 && (
            <span className="text-xs font-medium text-rose-700 bg-rose-100 rounded px-2 py-0.5" data-testid="orders-bulk-multi-customer-warning">
              {isEs ? `Hay ${selectedCustomers.size} clientes distintos: selecciona solo uno` : `${selectedCustomers.size} different customers: select only one`}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected({})} data-testid="orders-bulk-clear">
              <X className="h-4 w-4 mr-1" />{isEs ? 'Quitar selección' : 'Clear'}
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={invoiceSelected} disabled={selectedCustomers.size > 1} data-testid="orders-bulk-invoice-btn">
              <FileStack className="h-4 w-4 mr-1" />{isEs ? 'Facturar seleccionados' : 'Invoice selected'}
            </Button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <Card className="shadow-md border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="text-sm w-full [&_td]:py-2 [&_td]:px-2 [&_th]:py-2 [&_th]:px-2">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {onInvoiceSelected && (
                  <TableHead className="w-8">
                    <Checkbox checked={allPageSelected} onCheckedChange={togglePage} disabled={!selectable.length}
                      title={isEs ? 'Seleccionar los pedidos sin factura de esta página' : 'Select non-invoiced orders on this page'} data-testid="orders-select-page" />
                  </TableHead>
                )}
                <SortableTableHeader field="order_number" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                  {t.orderNumber}
                </SortableTableHeader>
                <TableHead className="hidden xl:table-cell">{t.externalOrderNumber}</TableHead>
                <SortableTableHeader field="customer_name" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                  {t.customer}
                </SortableTableHeader>
                <TableHead>{t.productUrl}</TableHead>
                <TableHead>{t.status}</TableHead>
                <TableHead className="hidden lg:table-cell">{language === 'es' ? 'URL Pub.' : 'Pub. URL'}</TableHead>
                <TableHead>{t.paymentStatus}</TableHead>
                <TableHead className="whitespace-nowrap">{language === 'es' ? 'Factura' : 'Invoice'}</TableHead>
                <SortableTableHeader field="pvp" sortField={sortField} sortOrder={sortOrder} onSort={handleSort}>
                  <span className="whitespace-nowrap">PVP / <span className="text-rose-600">PC</span></span>
                </SortableTableHeader>
                <TableHead className="hidden xl:table-cell">{t.writer}</TableHead>
                <SortableTableHeader field="order_date" sortField={sortField} sortOrder={sortOrder} onSort={handleSort} className="hidden xl:table-cell">
                  {t.orderDate}
                </SortableTableHeader>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-8 text-muted-foreground">
                    {t.noOrders}
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} className={`hover:bg-slate-50/50 ${selected[order.id] ? 'bg-emerald-50/60' : ''}`}>
                    {onInvoiceSelected && (
                      <TableCell>
                        <Checkbox checked={!!selected[order.id]} onCheckedChange={() => toggleOrder(order)} disabled={!canSelect(order)}
                          title={canSelect(order) ? '' : (isEs ? 'Ya facturado' : 'Already invoiced')} data-testid={`order-select-${order.order_number}`} />
                      </TableCell>
                    )}
                    <TableCell className="font-bold text-indigo-600">#{order.order_number}</TableCell>
                    <TableCell className="text-muted-foreground hidden xl:table-cell">{order.external_order_number || '-'}</TableCell>
                    <TableCell>
                      <div className="w-36 max-w-full">
                        <div className="truncate font-medium" title={order.customer_name}>{order.customer_name}</div>
                        <div className="text-xs text-muted-foreground truncate hidden lg:block" title={order.customer_email}>{order.customer_email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="w-52 max-w-full">
                        <div className="truncate text-xs" title={order.product_url}>{order.product_url}</div>
                        <div className="flex flex-wrap gap-1 mt-1 min-w-0" data-testid={`product-info-${order.order_number}`}>
                          {order.product_link_type ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                              {order.product_link_type}
                            </span>
                          ) : null}
                          {isAdLabelYes(order.product_ad_label) ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800" title={order.product_ad_label}>
                              {language === 'es' ? 'Etiqueta: Sí' : 'Tag: Yes'}
                            </span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                              {language === 'es' ? 'Etiqueta: No' : 'Tag: No'}
                            </span>
                          )}
                          {Array.isArray(order.product_sensitive_topics) && order.product_sensitive_topics.length > 0 && (
                            <>
                              {order.product_sensitive_topics.slice(0, 3).map((tk) => (
                                <span key={tk} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                                  {getSensitiveTopicLabel(tk, language)}
                                </span>
                              ))}
                              {order.product_sensitive_topics.length > 3 && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200"
                                  title={order.product_sensitive_topics.slice(3).map(tk => getSensitiveTopicLabel(tk, language)).join(', ')}
                                >
                                  +{order.product_sensitive_topics.length - 3}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={order.status} 
                        onValueChange={(v) => handleStatusChange(order.id, v)}
                      >
                        <SelectTrigger className={`w-[110px] h-8 text-xs ${STATUS_COLORS[order.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ORDER_STATUSES.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell data-testid={`order-published-url-${order.order_number}`} className="hidden lg:table-cell">
                      {order.published_url ? (
                        <a
                          href={order.published_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline truncate inline-block max-w-[140px]"
                          title={order.published_url}
                        >
                          {(() => {
                            try {
                              const u = new URL(order.published_url);
                              return `${u.hostname}${u.pathname.length > 1 ? '/...' : ''}`;
                            } catch {
                              return order.published_url.length > 22 ? order.published_url.slice(0, 22) + '...' : order.published_url;
                            }
                          })()}
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={order.payment_status} 
                          onValueChange={(v) => handleUpdateOrderStatus(order.id, 'payment_status', v)}
                        >
                          <SelectTrigger className={`w-[100px] h-8 text-xs ${PAYMENT_COLORS[order.payment_status]}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_STATUSES.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {viewOrderExpense && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => viewOrderExpense(order.id)}
                            title={
                              order.has_invoice_file
                                ? (language === 'es' ? 'Ver factura de gasto (pagada)' : 'View expense invoice (paid)')
                                : (language === 'es' ? 'Sin factura — gasto no pagado' : 'No invoice — expense unpaid')
                            }
                            className={
                              'h-7 w-7 p-0 ' + (order.has_invoice_file
                                ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
                                : 'text-rose-500 hover:text-rose-600 hover:bg-rose-50')
                            }
                            data-testid={`view-expense-${order.order_number}`}
                          >
                            <Receipt className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {(() => {
                        const s = order.invoice_status || 'sin_factura';
                        const cfg = {
                          sin_factura: { color: 'bg-rose-100 text-rose-700 border-rose-200', label: language === 'es' ? 'Sin factura' : 'Not invoiced' },
                          facturado_pendiente: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: language === 'es' ? 'Pdte. cobro' : 'Unpaid' },
                          facturado_cobrado: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: language === 'es' ? 'Cobrado' : 'Paid' },
                        }[s] || { color: 'bg-slate-100 text-slate-600', label: '—' };
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${cfg.color}`}
                            title={order.invoice_number ? `Factura: ${order.invoice_number}` : ''}
                            data-testid={`invoice-status-${order.order_number}`}
                          >
                            {cfg.label}
                            {order.invoice_number && <span className="opacity-70 hidden xl:inline">· {order.invoice_number}</span>}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap">
                      <div className="text-green-700">€{order.pvp}</div>
                      <div className="flex items-center gap-1.5 text-rose-600 text-xs">
                        <span>€{order.pc ?? 0}</span>
                        {order.product_pc_currency && order.product_pc_currency !== 'EUR' && (order.pc ?? 0) > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                            title={language === 'es'
                              ? `Pagar al editor en ${order.product_pc_currency}`
                              : `Pay editor in ${order.product_pc_currency}`}
                            data-testid={`pc-currency-${order.order_number}`}
                          >
                            {order.product_pc_currency === 'USD' ? '$' : order.product_pc_currency === 'GBP' ? '£' : ''}
                            {order.pc} {order.product_pc_currency}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      <div className="w-24 truncate" title={order.writer}>{order.writer}</div>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-xs">{order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openOrderDialog(order)} className="text-slate-600 hover:text-indigo-600 hover:bg-indigo-50">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteOrder(order.id)} className="text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Pagination
          pagination={pagination}
          onPageChange={(page) => fetchOrders(page)}
          language={language}
        />
      )}
    </div>
  );
};

export default OrdersView;
